import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, App, Spin } from 'antd';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { useContratoForm, ContratoFormData } from '../../hooks/useContratoForm';
import { usePersonas, Persona } from '../../hooks/usePersonas';
import { ContratoFormHeader } from './contratos/ContratoFormHeader';
import { DatosBasicosContrato } from './contratos/DatosBasicosContrato';
import { PersonasContrato } from './contratos/PersonasContrato';
import { PersonasOpcionales } from './contratos/PersonasOpcionales';
import { DetallesContrato } from './contratos/DetallesContrato';
import { ContratoFormActions } from './contratos/ContratoFormActions';

const fechaActual = new Date().toISOString().split('T')[0];

const AltaContratos: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  
  // Hooks personalizados
  const {
    formData,
    setFormData,
    productos,
    loadingCatalogos,
    hasUnsavedChanges,
    selectedProducto,
    resetForm
  } = useContratoForm();
  
  const { personas, loading: loadingPersonas } = usePersonas();
  
  // Estados para personas seleccionadas
  const [selectedComprador, setSelectedComprador] = useState<Persona | null>(null);
  const [selectedVendedor, setSelectedVendedor] = useState<Persona | null>(null);
  const [selectedDestinatario, setSelectedDestinatario] = useState<Persona | null>(null);
  const [selectedDespachante, setSelectedDespachante] = useState<Persona | null>(null);
  const [selectedCorredor, setSelectedCorredor] = useState<Persona | null>(null);
  const [selectedEntregador, setSelectedEntregador] = useState<Persona | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [saveAndNew, setSaveAndNew] = useState(false);
  const initialValuesRef = useRef<any>(null);

  // Inicializar valores iniciales
  useEffect(() => {
    if (!loadingCatalogos && !loadingPersonas) {
      const initialValues = {
        ...formData,
        comprador: selectedComprador?.id || 0,
        vendedor: selectedVendedor?.id || 0,
        destinatario: selectedDestinatario?.id || 0,
        despachante: selectedDespachante?.id || 0,
        corredor: selectedCorredor?.id || 0,
        entregador: selectedEntregador?.id || 0
      };
      initialValuesRef.current = initialValues;
    }
  }, [loadingCatalogos, loadingPersonas]);

  // Manejar cambio de tipo de contrato (auto-seleccionar padrón 000000)
  useEffect(() => {
    if (formData.tipo === 'C' && personas.length > 0) {
      const padronCero = personas.find(p => p.nc === '000000');
      if (padronCero && !selectedComprador) {
        setSelectedComprador(padronCero);
        setFormData(prev => ({ ...prev, comprador: padronCero.id }));
      }
    } else if (formData.tipo === 'V' && personas.length > 0) {
      const padronCero = personas.find(p => p.nc === '000000');
      if (padronCero && !selectedVendedor) {
        setSelectedVendedor(padronCero);
        setFormData(prev => ({ ...prev, vendedor: padronCero.id }));
      }
    } else if (formData.tipo === '') {
      // Limpiar selecciones si se quita el tipo
      if (selectedComprador?.nc === '000000') {
        setSelectedComprador(null);
        setFormData(prev => ({ ...prev, comprador: 0 }));
      }
      if (selectedVendedor?.nc === '000000') {
        setSelectedVendedor(null);
        setFormData(prev => ({ ...prev, vendedor: 0 }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipo, personas]);

  // Forzar Tara Fardo a 0 si el producto es tipo 3
  useEffect(() => {
    if (selectedProducto && selectedProducto.tipo === 3 && formData.tarafardo !== 0) {
      setFormData(prev => ({ ...prev, tarafardo: 0 }));
    }
  }, [selectedProducto, formData.tarafardo]);

  // Calcular progreso del formulario
  const formProgress = useMemo(() => {
    const totalFields = 7; // Campos requeridos principales
    let completedFields = 0;

    if (formData.ncontrato) completedFields++;
    if (formData.tipo) completedFields++;
    if (formData.producto_id) completedFields++;
    if (selectedComprador) completedFields++;
    if (selectedVendedor) completedFields++;
    if (selectedDestinatario) completedFields++;
    if (formData.kilos && formData.kilos > 0) completedFields++;

    return Math.round((completedFields / totalFields) * 100);
  }, [formData, selectedComprador, selectedVendedor, selectedDestinatario]);

  // Manejar cambios en campos del formulario
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Manejar selección de personas
  const handleCompradorSelect = (persona: Persona | null) => {
    setSelectedComprador(persona);
    setFormData(prev => ({
      ...prev,
      comprador: persona?.id || 0
    }));
  };

  const handleVendedorSelect = (persona: Persona | null) => {
    setSelectedVendedor(persona);
    setFormData(prev => ({
      ...prev,
      vendedor: persona?.id || 0
    }));
  };

  const handleDestinatarioSelect = (persona: Persona | null) => {
    setSelectedDestinatario(persona);
    setFormData(prev => ({
      ...prev,
      destinatario: persona?.id || 0
    }));
  };

  const handleDespachanteSelect = (persona: Persona | null) => {
    setSelectedDespachante(persona);
    setFormData(prev => ({
      ...prev,
      despachante: persona?.id || 0
    }));
  };

  const handleCorredorSelect = (persona: Persona | null) => {
    setSelectedCorredor(persona);
    setFormData(prev => ({
      ...prev,
      corredor: persona?.id || 0
    }));
  };

  const handleEntregadorSelect = (persona: Persona | null) => {
    setSelectedEntregador(persona);
    setFormData(prev => ({
      ...prev,
      entregador: persona?.id || 0
    }));
  };

  // Función para guardar contrato
  const handleSave = async (saveAndContinue: boolean = false) => {
    setLoading(true);
    setSaveAndNew(saveAndContinue);
    
    try {
      const id_empresa = user?.id_empresa;
      if (!id_empresa) {
        message.error('No se encontró el id_empresa del usuario');
        return;
      }

      // Validar campos obligatorios
      if (!selectedComprador) {
        message.error('Debe seleccionar un Comprador');
        setLoading(false);
        return;
      }
      if (!selectedVendedor) {
        message.error('Debe seleccionar un Vendedor');
        setLoading(false);
        return;
      }
      if (!selectedDestinatario) {
        message.error('Debe seleccionar un Destinatario');
        setLoading(false);
        return;
      }
      if (!formData.kilos || formData.kilos <= 0) {
        message.error('Debe ingresar un valor válido para Kilos');
        setLoading(false);
        return;
      }

      // Crear objeto con los datos del formulario
      const formDataToSend: any = {
        ...formData,
        fecha: formData.fecha || fechaActual,
        fecha_contrato: formData.fecha_contrato || fechaActual,
        fecha_cierre: formData.fecha_cierre || fechaActual,
        id_comprador: selectedComprador.id,
        comprador: selectedComprador.id,
        id_vendedor: selectedVendedor.id,
        vendedor: selectedVendedor.id,
        id_destinatario: selectedDestinatario.id,
        destinatario: selectedDestinatario.id,
        id_despachante: selectedDespachante ? selectedDespachante.id : 0,
        despachante: selectedDespachante ? selectedDespachante.id : 0,
        id_corredor: selectedCorredor ? selectedCorredor.id : 0,
        corredor: selectedCorredor ? selectedCorredor.id : 0,
        id_entregador: selectedEntregador ? selectedEntregador.id : 0,
        entregador: selectedEntregador ? selectedEntregador.id : 0,
      };

      const token = await getAccessTokenSilently();
      const response = await apiClient.post(
        `${process.env.REACT_APP_ORDENES_URL}/alta_contrato/${id_empresa}/`,
        formDataToSend,
        { token }
      );

      message.success(saveAndContinue 
        ? 'Contrato creado exitosamente. Puede continuar cargando otro contrato.'
        : 'Contrato creado exitosamente'
      );

      if (saveAndContinue) {
        // Limpiar formulario y mantener en la página
        resetForm();
        setSelectedComprador(null);
        setSelectedVendedor(null);
        setSelectedDestinatario(null);
        setSelectedDespachante(null);
        setSelectedCorredor(null);
        setSelectedEntregador(null);
        const resetData = {
          ncontrato: '',
          tipo: '',
          producto_id: 0,
          fecha: fechaActual,
          comprador: 0,
          vendedor: 0,
          despachante: 0,
          corredor: 0,
          entregador: 0,
          destinatario: 0,
          tarafardo: 0,
          kilos: 0,
          precio: 0,
          fecha_contrato: fechaActual,
          fecha_cierre: fechaActual
        };
        initialValuesRef.current = resetData;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Navegar de vuelta al listado
        setTimeout(() => {
          navigate('/listadocontratos');
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error al crear contrato:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || 'Error desconocido';
      message.error(`Error al crear el contrato: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/listadocontratos');
  };

  if (loadingCatalogos || loadingPersonas) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Cargando datos..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <ContratoFormHeader formProgress={formProgress} />

      <Card
        style={{
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600,
            padding: '12px 16px'
          },
          body: {
            padding: '24px'
          }
        }}
        title="Información del Contrato"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={() => handleSave(false)}
          style={{ maxWidth: '100%' }}
          className="contrato-form"
        >
          <style>{`
            .contrato-form .ant-form-item-label > label {
              color: ${theme.colors.textPrimary} !important;
              font-weight: 500 !important;
              font-size: 13px !important;
              line-height: 1.4 !important;
            }
            .contrato-form .ant-form-item {
              margin-bottom: 16px !important;
            }
            .contrato-form .ant-form-item-required::before {
              color: ${theme.colors.error} !important;
            }
            .contrato-form .ant-input,
            .contrato-form .ant-select-selector,
            .contrato-form .ant-input-number,
            .contrato-form .ant-picker {
              border-color: ${theme.colors.borderLight} !important;
              border-radius: 6px !important;
            }
            .contrato-form .ant-input:focus,
            .contrato-form .ant-select-selector:focus,
            .contrato-form .ant-input-number:focus,
            .contrato-form .ant-picker:focus {
              border-color: ${theme.colors.primary} !important;
              box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
            }
          `}</style>

          {/* Datos Básicos */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: theme.colors.primaryDark, 
              fontSize: '16px', 
              fontWeight: 600, 
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${theme.colors.borderLight}`
            }}>
              Datos Básicos
            </h3>
            <DatosBasicosContrato
              productos={productos}
              formData={{
                ncontrato: formData.ncontrato,
                tipo: formData.tipo,
                producto_id: formData.producto_id,
                fecha: formData.fecha
              }}
              onFieldChange={handleFieldChange}
            />
          </div>

          {/* Personas Obligatorias */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: theme.colors.primaryDark, 
              fontSize: '16px', 
              fontWeight: 600, 
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${theme.colors.borderLight}`
            }}>
              Personas del Contrato
            </h3>
            <PersonasContrato
              personas={personas}
              selectedComprador={selectedComprador}
              selectedVendedor={selectedVendedor}
              selectedDestinatario={selectedDestinatario}
              tipoContrato={formData.tipo}
              onCompradorSelect={handleCompradorSelect}
              onVendedorSelect={handleVendedorSelect}
              onDestinatarioSelect={handleDestinatarioSelect}
            />
          </div>

          {/* Personas Opcionales */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: theme.colors.primaryDark, 
              fontSize: '16px', 
              fontWeight: 600, 
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${theme.colors.borderLight}`
            }}>
              Personas Opcionales
            </h3>
            <PersonasOpcionales
              personas={personas}
              selectedDespachante={selectedDespachante}
              selectedCorredor={selectedCorredor}
              selectedEntregador={selectedEntregador}
              onDespachanteSelect={handleDespachanteSelect}
              onCorredorSelect={handleCorredorSelect}
              onEntregadorSelect={handleEntregadorSelect}
            />
          </div>

          {/* Detalles del Contrato */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              color: theme.colors.primaryDark, 
              fontSize: '16px', 
              fontWeight: 600, 
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${theme.colors.borderLight}`
            }}>
              Detalles del Contrato
            </h3>
            <DetallesContrato
              formData={{
                tarafardo: formData.tarafardo,
                kilos: formData.kilos,
                precio: formData.precio,
                fecha_contrato: formData.fecha_contrato,
                fecha_cierre: formData.fecha_cierre
              }}
              productoType={selectedProducto?.tipo}
              onFieldChange={handleFieldChange}
            />
          </div>

          {/* Botones de acción */}
          <ContratoFormActions
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={() => handleSave(false)}
            onSaveAndNew={() => handleSave(true)}
            onCancel={handleCancel}
          />
        </Form>
      </Card>
    </div>
  );
};

export default AltaContratos;
